import React from 'react';
import { TrendingUp, DollarSign, Activity } from 'lucide-react';
import { Synergy } from '../../types/integration';
import { formatCurrency } from '../../utils/formatters';

interface SynergyTrackerProps {
  synergies: Synergy[];
}

export default function SynergyTracker({ synergies }: SynergyTrackerProps) {
  const totalTarget = synergies.reduce((sum, s) => sum + s.targetValue, 0);
  const totalRealized = synergies.reduce((sum, s) => sum + s.actualValue, 0);
  const realizationRate = totalTarget ? (totalRealized / totalTarget) * 100 : 0;

  const synergyTypes = {
    cost: synergies.filter(s => s.type === 'cost'),
    revenue: synergies.filter(s => s.type === 'revenue'),
    operational: synergies.filter(s => s.type === 'operational')
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Total Target</h3>
            <DollarSign className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalTarget)}</p>
          <p className="mt-1 text-sm text-gray-500">Expected synergies</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Realized Value</h3>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalRealized)}</p>
          <p className="mt-1 text-sm text-gray-500">Achieved to date</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Realization Rate</h3>
            <Activity className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{realizationRate.toFixed(1)}%</p>
          <p className="mt-1 text-sm text-gray-500">Of target value</p>
        </div>
      </div>

      {/* Synergy Categories */}
      <div className="space-y-4">
        {Object.entries(synergyTypes).map(([type, items]) => {
          const typeTarget = items.reduce((sum, s) => sum + s.targetValue, 0);
          const typeRealized = items.reduce((sum, s) => sum + s.actualValue, 0);
          const typeRate = typeTarget ? (typeRealized / typeTarget) * 100 : 0;

          return (
            <div key={type} className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-900 capitalize">{type} Synergies</h4>
                <span className="text-sm text-gray-500">{items.length} initiatives</span>
              </div>

              <div className="space-y-4">
                {items.map(synergy => (
                  <div key={synergy.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">{synergy.description}</p>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        synergy.status === 'realized' 
                          ? 'bg-green-100 text-green-800'
                          : synergy.status === 'in_progress'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {synergy.status}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        Target: {formatCurrency(synergy.targetValue)}
                      </span>
                      <span className="text-gray-500">
                        Realized: {formatCurrency(synergy.actualValue)}
                      </span>
                    </div>

                    <div className="relative pt-1">
                      <div className="flex mb-2 items-center justify-between">
                        <div>
                          <span className="text-xs font-semibold inline-block text-blue-600">
                            {((synergy.actualValue / synergy.targetValue) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="overflow-hidden h-2 text-xs flex rounded bg-blue-100">
                        <div
                          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-600"
                          style={{ width: `${(synergy.actualValue / synergy.targetValue) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-900">
                    Total Progress: {typeRate.toFixed(1)}%
                  </span>
                  <span className="text-gray-500">
                    {formatCurrency(typeRealized)} of {formatCurrency(typeTarget)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}